<script type="text/ecmascript-6">
import Vue from "vue";
import { mapActions, mapGetters } from "vuex";
import { AxiosGetSignatures } from "Api/public/axios";
import VueResize from "vue-resize";
import GeneralMixin from "JS/mixins/campaign/general";
import TrackingMixin from "JS/mixins/campaign/tracking";
import EventsMixin from "JS/mixins/campaign/events";
import FormValidationMixin from "JS/mixins/campaign/formValidation";
import VeeValidate from "vee-validate";
import RedirectHelper from "JS/helpers/redirectHelper";
import Twitter from "JS/helpers/_twitter";
import AxiosHelper from "JS/helpers/axios";
import { SlideXTransition } from "JS/transitions/transitions";
import WidgetHelper from "JS/helpers/widgetHelper";
import htmlHelper from "JS/helpers/htmlHelper";
import { keys as _keys } from "lodash";
import { i18n, loadLanguageAsync } from "JS/i18n";
import FormHelper from "JS/helpers/FormHelper";
// Sombra page elements
import SombraHeader from "./Header.vue";
import SombraNav from "./Nav.vue";
import SombraContent from "./Content.vue";
import SombraFormFields from "./FormFields.vue";
import SombraFormSubmit from "./FormSubmit.vue";
import SombraAdvocateBadge from "./AdvocateBadge.vue";
import SombraLegislatorResults from "./LegislatorResults.vue";
import SombraOptIn from "./OptIn.vue";
import SombraSignaturesCounter from "./SignaturesCounter.vue";
import SombraSignaturesList from "./SignaturesList.vue";
import SombraThankYou from "./ThankYou.vue";
import SombraFooter from "./Footer.vue";
// conversational
import CardPrimary from "../conversational/card/CardPrimary.vue";
import SombraErrorOverlay from "../conversational/modal/ErrorOverlay.vue";
import ConversationalIcons from "../conversational/icons/ConversationalIcons.vue";
import CookieReset from "../conversational/cookie/CookieReset.vue";

Vue.use(VueResize);
Vue.use(VeeValidate, { events: "blur", inject: false });

const twitterHelper = Twitter();

const redirectHelper = new RedirectHelper();
const widgetHelper = new WidgetHelper();
const eventsBus = new Vue({});

export default {
  name: "Sombra",
  components: {
    "sombra-header": SombraHeader,
    "sombra-nav": SombraNav,
    "sombra-content": SombraContent,
    "sombra-form-fields": SombraFormFields,
    "sombra-form-submit": SombraFormSubmit,
    "sombra-legislator-results": SombraLegislatorResults,
    "sombra-advocate-badge": SombraAdvocateBadge,
    "sombra-opt-in": SombraOptIn,
    "sombra-signatures-counter": SombraSignaturesCounter,
    "sombra-signatures-list": SombraSignaturesList,
    "sombra-thank-you": SombraThankYou,
    "sombra-footer": SombraFooter,
    "slide-x-transition": SlideXTransition,
    icons: ConversationalIcons,
    CardPrimary,
    "sombra-error-overlay": SombraErrorOverlay,
    CookieReset
  },
  i18n,
  mixins: [GeneralMixin, TrackingMixin, EventsMixin, FormValidationMixin],
  props: {
    advocateInfo: {
      required: true,
      type: Object
    },
    customerSettings: {
      type: Object,
      required: true
    },
    customFieldsValues: {
      type: Object,
      required: true
    },
    settings: {
      type: Object,
      required: true
    },
    appSettings: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      clientWidth: 0,
      initialLegislatorResults: {
        email: [
          {
            legislators: [],
            message: this.settings.emailMessages[0]
          }
        ]
      },
      isWidget: widgetHelper.isWidget(this.appSettings),
      mobileBreakpoint: 768,
      trackingPixelImages: []
    };
  },
  computed: {
    ...mapGetters({
      actionCompleted: "navigation/actionCompleted",
      allActionsAlreadyTaken: "navigation/allActionsAlreadyTaken",
      cookieBadgeVisible: "forms/cookieBadgeVisible",
      descriptionVisible: "navigation/campaignDescriptionVisible"
    }),
    textPlaceholders() {
      return this.settings.textPlaceholders || {};
    },
    advocateBadge() {
      return {
        welcomeBackLabel: this.settings.cookieWelcomeBack,
        wrongInfoLabel: this.settings.cookieWrongInformation
      };
    },
    assignConversationalCardClass() {
      if (!this.completedAllActions && !this.legislatorsPulled) {
        return "card--step";
      } else if (
        !this.completedAllActions &&
        this.legislatorsPulled &&
        this.expandedLegislatorLists.length &&
        !this.isMobile
      ) {
        return "card--leg-results";
      }

      return [
        "card--action",
        `card--${this.currentActionCard}`,
        {
          "card--multi-message": this.isMultiMessage
        }
      ];
    },
    formClass() {
      return "p2a-action-form-email";
    },
    isEmailOrPhoneStep() {
      return this.currentStep === "email" || this.currentStep === "phone";
    },
    mountLegislatorResults() {
      return this.currentAction !== "default";
    },
    legislatorResultsSettings() {
      return {
        callToActionHeading: this.settings.callToActionHeading,
        contactLegislatorsPrompt: this.settings.contactLegislatorsPrompt,
        contactPhoneScript: this.settings.contactPhoneScript,
        hasOneStep: this.settings.hasOneStep,
        emailLocked: this.settings.emailLocked,
        emailEditRequired: this.settings.emailEditRequired,
        messagesPlaceHolder: this.settings.messagesPlaceHolder,
        messagesSuggestionPrompt: this.settings.messagesSuggestionPrompt,
        seeDetailedResultsPrompt: this.settings.seeDetailedResultsPrompt,
        noLegislatorsFoundErrorMessage: this.settings
          .noLegislatorsFoundErrorMessage,
        scriptHeadline: this.settings.scriptHeadline,
        shortLegislatorListLength: this.settings.shortLegislatorListLength,
        twitterActionFooterLabel: this.settings.twitterActionFooterLabel,
        multiMessageLabels: this.settings.multiMessageLabels,
        ofLabel: this.settings.ofLabel,
        includeWebIntentMedia: this.settings.includeWebIntentMedia
      };
    },
    introductionMobile() {
      if (this.settings.hideMobileIntroduction) {
        return "";
      }
      if (this.settings.enMobileCustomDescription) {
        return this.settings.contentIntroductionMobile;
      }
      return this.settings.contentIntroduction;
    },
    campaign() {
      return {
        actions: {
          enabledFields: this.settings.formFields,
          styles: {
            email: {
              class: "action-email",
              label: "Email",
              ariaLabel: "Send Email"
            },
            twitter: {
              class: "action-twitter",
              label: "Tweet",
              ariaLabel: "Post to Twitter"
            },
            call: {
              class: "action-phone",
              label: "Call",
              ariaLabel: "Call Officials"
            }
          }
        },
        andLabel: this.settings.andLabel,
        noLabel: this.settings.noLabel,
        yesLabel: this.settings.yesLabel,
        badgeDisplay: this.settings.badgeDisplay,
        cityStateNotRequiredLabel: this.settings.cityStateNotRequiredLabel,
        contentBackgroundColor: this.settings.campaignDesignColors
          .mainContentBackground,
        copyright: this.settings.copyright,
        countryDefault: this.settings.fieldsCountryDefault,
        footerBackgroundColor: this.settings.campaignDesignColors
          .footerBackground,
        formBackgroundColorTop: this.settings.campaignDesignColors
          .formBackgroundTop,
        formBackgroundColorBottom: this.settings.campaignDesignColors
          .formBackgroundBottom,
        headerBackgroundColor: this.settings.campaignDesignColors
          .headerBackground,
        mainTextColor: this.settings.campaignDesignColors.mainContentTextColor,
        formTextColor: this.settings.campaignDesignColors.formTextColor,
        advocateMessageRequiredPrompt: this.settings
          .advocateMessageRequiredPrompt,
        subTitle: this.settings.subTitle,
        cardDescriptions: this.settings.cardDescriptions,
        cardHeadlines: this.settings.cardHeadlines,
        cardProgressLabels: this.settings.cardProgressLabels,
        cardSubheadlines: this.settings.cardSubheadlines,
        callToActionHeading: this.settings.callToActionHeading,
        callInstructions: this.settings.callInstructions,
        callProgressButtonLabel: this.settings.callProgressButtonLabel,
        campaignDesignColors: this.settings.campaignDesignColors,
        contentLabelSalutation: this.settings.contentLabelSalutation,
        cookieButtonLabels: this.settings.cookieButtonLabels,
        ctaTextColor: this.settings.campaignDesignColors.ctaText,
        actionButtonsText: this.settings.actionButtonsText,
        displayName: this.settings.displayName,
        descriptionCollapseLabel: this.settings.descriptionCollapseLabel,
        descriptionExpandLabel: this.settings.descriptionExpandLabel,
        getStartedLabel: this.settings.getStarted,
        nextStepLabel: this.settings.nextStep,
        greetingPrefix: this.settings.greetingPrefix,
        groupedCallInstructions: this.settings.groupedCallInstructions,
        isMultistep: this.settings.isMultistep,
        hidePrivacyPolicy: this.customerSettings.enHidePoliciesFrontend,
        showPrivacyConsentLanguage: this.customerSettings
          .showPrivacyConsentLanguage,
        legislatorHeadings: this.settings.legislatorHeadings,
        legislatorHeadingsCollapseLabel: this.settings
          .legislatorHeadingsCollapseLabel,
        logoImageSource: this.settings.campaignLogo,
        logoImageAltText: this.settings.campaignLogoAltText,
        contentIntroduction: this.settings.contentIntroduction,
        contentIntroductionMobile: this.introductionMobile,
        contactActionCenterEmailCTA: this.settings.contactActionCenterEmailCTA,
        enDefaultCollapsedDescription: this.settings
          .enDefaultCollapsedDescription,
        advocateCreateButtonText: this.settings.advocateCreateButtonText,
        facebookAppId: this.appSettings.facebookAppId,
        formFields: this.settings.formFields,
        stepsWithSubscription: ["email", "phone"],
        campaignRestrictedMessage: {
          restrictedMessageBody: this.settings.restrictedMessageBody,
          restrictedMessageEnding: this.settings.restrictedMessageEnding
        },
        additionalFieldsPrompt: this.settings.additionalFieldsPrompt,
        pageBackground: this.settings.pageBackground,
        phoneGreeting: this.settings.phoneGreeting,
        phoneGreetingCustom: this.settings.phoneGreetingCustom,
        privacyConsentLanguage: this.settings.privacyConsentLanguage,
        privacyPolicyLabel: this.settings.privacyPolicyLabel,
        defaultPrivacyConsentLanguage: this.settings
          .defaultPrivacyConsentLanguage,
        defaultPrivacyConsentLanguageAccordance: this.settings
          .defaultPrivacyConsentLanguageAccordance,
        moveOnText: this.settings.moveOnText,
        privacyPolicyDisclaimer:
          this.settings.campaignLevelDisclaimer ||
          this.customerSettings.privacyPolicyDisclaimer,
        privacyPolicyUrl: this.customerSettings.privacyPolicyUrl,
        privacyPolicyUrlLabel: this.customerSettings.privacyPolicyUrlLabel,
        redirectUrl: this.settings.contactCompleteRedirectURL,
        redirectDelay: this.settings.redirectDelay,
        requiredFieldsLabel: this.settings.requiredFieldsLabel,
        skipLabel: this.settings.skipStepLabel,
        shareLabel: this.settings.socialShare.shareLabel,
        showLegislatorDetailsLabel: this.settings.showLegislatorDetailsLabel,
        showRemainingLegislatorsLabel: this.settings
          .showRemainingLegislatorsLabel,
        showProgressCounter: this.settings.showProgressCounter,
        socialShare: this.settings.socialShare,
        socialShareEnabled: this.settings.socialShare.socialShareEnabled,
        socialSignIn: this.settings.socialSignIn || {},
        shareOptions: this.settings.shareOptions,
        facebookShareEnabled: this.settings.facebookShareEnabled,
        tryAgainText: this.settings.tryAgainText,
        twitterShareEnabled: this.settings.twitterShareEnabled,
        type: this.settings.type,
        shareLabels: {
          facebook: this.settings.shareLabelFacebook,
          twitter: this.settings.shareLabelTwitter
        },
        showCampaignDescription: this.settings.showContentIntroduction,
        subjectTo: this.settings.subjectTo,
        successMessages: this.settings.successMessages,
        successImages: this.settings.successImages,
        thankYouImage: this.settings.thankYouImage,
        thankYouMessage: this.settings.thankYouMessage,
        signatures: this.settings.signatures || {},
        subscribeEmailLabel: this.settings.subscribeEmailLabel,
        subscribeSmsLabel: this.settings.subscribeSmsLabel,
        subscribeEmail: this.settings.subscribeEmail,
        subscribeSms: this.settings.subscribeSms,
        submitButtonClass: {
          email: !this.settings.campaignDesignColors.hideSendButtonIcon
            ? "p2a-button-icon p2a-button-icon-email"
            : "",
          twitter: !this.settings.campaignDesignColors.hideSendButtonIcon
            ? "p2a-button-icon p2a-button-icon-twitter"
            : "",
          call: !this.settings.campaignDesignColors.hideSendButtonIcon
            ? "p2a-button-icon p2a-button-icon-phone"
            : ""
        },
        submitButtonLabels: this.settings.submitButtonLabels,
        previewModeAlert: this.settings.previewModeAlert,
        previewMode: this.settings.previewMode,
        progressBarColor: this.settings.progressBarColor,
        charactersRemainingLabel: this.settings.charactersRemainingLabel,
        shouldShowSubmitButtons:
          this.settings.submitButtonDisplaySettings || {},
        buttonEmailReverse: this.settings.buttonEmailReverse || false,
        tweetPostedLabel: this.settings.tweetPostedLabel,
        tooltips: this.settings.tooltips,
        useTwitterWebIntents: this.settings.useTwitterWebIntents,
        advancedAccessibility: this.settings.advancedAccessibility,
        emailEditRequired: this.settings.emailEditRequired,
        advocateCampaignShareUrl: this.settings.advocateCampaignShareUrl
      };
    },
    actionSectionStyles() {
      if (!this.isWidget) {
        return {
          background: this.setFormBackgroundGradient,
          borderColor: this.campaign.formBackgroundColorBottom
        };
      }
      return null;
    },
    contentStyles() {
      return {
        background: this.campaign.contentBackgroundColor,
        color: this.campaign.mainTextColor
      };
    },
    headerStyles() {
      return {
        background: this.campaign.headerBackgroundColor
      };
    },
    isEmailCardActive() {
      return this.currentStep === "email" && !this.legislatorsPulled;
    },
    isMobile() {
      return this.clientWidth < this.mobileBreakpoint;
    },
    isPhoneCardActive() {
      return this.currentStep === "phone" && !this.legislatorsPulled;
    },
    formStyles() {
      return {
        color: this.campaign.formTextColor
      };
    },
    footerStyles() {
      const convoDesktop = this.appSettings.theme === "Convo" && !this.isMobile;
      if (!this.isWidget && !convoDesktop) {
        return {
          background: this.campaign.footerBackgroundColor,
          color: this.campaign.mainTextColor
        };
      }
      return {
        color: this.campaign.formTextColor
      };
    },
    navStyles() {
      return {
        color: this.campaign.formTextColor
      };
    },
    needAdditionalFields() {
      return this.$store.getters["forms/needAdditionalFields"][
        this.currentAction
      ];
    },
    shouldRenderPetitionComponents() {
      return (
        !this.completedAllActions &&
        this.settings.type === "PETITION" &&
        this.campaign.signatures.showCount === true
      );
    },
    shouldRenderPetitionList() {
      return (
        this.shouldRenderPetitionComponents &&
        this.campaign.signatures.count > 0
      );
    },
    setFormBackgroundGradient() {
      return `linear-gradient(${this.campaign.formBackgroundColorTop}, ${
        this.campaign.formBackgroundColorBottom
      })`;
    },
    showCallToAction() {
      return (
        this.descriptionVisible ||
        this.cookieBadgeVisible ||
        this.currentStep === "welcome"
      );
    },
    showFooter() {
      return (
        this.campaign.hidePrivacyPolicy === 0 ||
        (this.campaign.privacyPolicyUrl || this.campaign.privacyPolicyUrlLabel)
      );
    },
    showOneStep() {
      return this.settings.hasOneStep && this.currentAction === "email";
    },
    showLegislatorResults() {
      return (
        !this.completedAllActions &&
        !this.needAdditionalFields &&
        (this.legislatorsPulled || this.showOneStep)
      );
    },
    legislatorResultsStyle() {
      let display = "block";
      let order = "";
      if (this.showOneStep) {
        order = "order: 5;";
      } else if (!this.showLegislatorResults) {
        display = "none";
      }
      return `display: ${display}; ${order}`;
    },
    thankYouStyles() {
      return {
        color: this.campaign.formTextColor
      };
    }
  },
  watch: {
    actionCompleted() {
      // return to top of window once an action has been completed
      window.scrollTo({ top: 0 });
      this.trackFormCompleteEvent();

      // watchers in transitions are not firing events
      // emit event for these components
      eventsBus.$emit("actionCompleted");
    }
  },
  async created() {
    const locale = this.appSettings.language.toLowerCase();
    await loadLanguageAsync(locale);

    this.setStateFromSettings(this.settings);
  },
  beforeMount() {
    const firstAction = _keys(this.settings.actionDetails)[0];

    this.$store.dispatch("navigation/activeAction", firstAction);

    eventsBus.$emit("actionActivated", firstAction);

    this.$store.dispatch(
      "navigation/actionDetails",
      this.settings.actionDetails
    );

    // tracking
    eventsBus.$on("handleFormFieldValue", this.trackFormStartEvent);
    eventsBus.$on("actionActivated", () => {
      this.setFormStarted(false);
    });

    eventsBus.$on("sharedViaSocialNetwork", this.trackShareViaSocial);
    eventsBus.$on("findLegislators", payload => {
      if (!payload.background) {
        this.trackFoundLegislatorsEvent();
      }
    });

    this.resetTrackingState();
  },
  mounted() {
    this.setBackground();
    this.setInitialOptInStates();
    this.setInitialLegislatorResults();
    this.handleResize();
    this.setDescriptionCardState();
    this.getSignatures();
  },
  methods: {
    ...mapActions({
      setFormValue: "forms/formValues",
      setStateFromSettings: "campaign/fromSettings"
    }),
    closeSuccessModal() {
      if (this.getModalState) {
        this.setModalState(false);
      }
    },
    handleResize() {
      this.clientWidth = document.body.clientWidth;

      if (this.isWidget) {
        this.$nextTick(() => {
          const { templateId } = this.appSettings;
          widgetHelper.broadcastDimensions(templateId);
        });
      }
    },
    setBackground() {
      if (this.isWidget) {
        document.body.style.backgroundImage = this.setFormBackgroundGradient;
        return;
      }
      document.body.style.cssText = this.campaign.pageBackground;
    },
    async getSignatures() {
      if (!this.shouldRenderPetitionComponents) {
        return;
      }
      const response = await AxiosGetSignatures.get("");
      const { signatures, count, label } = response.data;

      this.settings.signatures = Object.assign({}, this.settings.signatures, {
        count,
        label,
        signatures
      });
    },
    setDescriptionCardState() {
      const contentIntroduction = htmlHelper(
        this.campaign.contentIntroduction
      ).trim();
      const descriptionVisible =
        this.campaign.showCampaignDescription && contentIntroduction.length;
      this.$store.dispatch(
        "navigation/campaignDescriptionVisible",
        descriptionVisible
      );
    },
    setInitialOptInStates() {
      let subscribeEmail = this.customerSettings.subscriptionDefaultEmail;
      let subscribeSms = this.customerSettings.subscriptionDefaultSms;
      let { stepsWithSubscription } = this.campaign;

      // check if subscribe email opt-in is disabled for campaign
      if (!this.campaign.subscribeEmail) {
        stepsWithSubscription = stepsWithSubscription.filter(
          step => step !== "email"
        );
        subscribeEmail = false;
      }

      // check if subscribe sms opt-in is disabled for campaign
      if (!this.campaign.subscribeSms) {
        stepsWithSubscription = stepsWithSubscription.filter(
          step => step !== "phone"
        );
        subscribeSms = false;
      }

      this.campaign.stepsWithSubscription = stepsWithSubscription;
      this.setFormValue({ fieldName: "subscribeEmail", value: subscribeEmail });
      this.setFormValue({ fieldName: "subscribeSms", value: subscribeSms });
    },
    setInitialLegislatorResults() {
      const initialResults = this.settings.hasOneStep
        ? this.initialLegislatorResults
        : {};
      this.$store.dispatch(
        "legislators/initialLegislatorResults",
        initialResults
      );
    }
  },
  provide() {
    return {
      eventsBus,
      redirectHelper,
      twitterHelper,
      axiosHelper: new AxiosHelper(this.settings.unknownErrorMessage),
      formHelper: new FormHelper(this.$store, this.settings.formFields),
      widgetHelper
    };
  }
};
</script>
